import { useEffect, useImperativeHandle, useMemo, useState, type Ref } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, PERMISSION, can, contactAttributes, fillQuickReply, inboxApi, useAuth,
  type CannedReply, type QuickReplyContext,
} from '@wa/shared';

/** A quick reply's file, handed to the composer to send with the text. */
export interface QuickReplyAttachment {
  mediaAssetId: number;
  fileName: string;
  mimeType: string | null;
}

function attachmentOf(reply: CannedReply): QuickReplyAttachment | null {
  return reply.mediaAssetId
    ? { mediaAssetId: reply.mediaAssetId, fileName: reply.mediaFileName ?? 'file', mimeType: reply.mediaMimeType }
    : null;
}

interface Props {
  /** The text after the leading slash, or null when the panel is closed. */
  query: string | null;
  contactName: string | null;
  /** For {{field.key}}; the contact is fetched only while the panel is open. */
  contactId?: number | null;
  /** The current draft, offered for saving when an admin has typed something. */
  draft: string;
  onPick: (text: string, attachment: QuickReplyAttachment | null) => void;
  onClose: () => void;
  /** Keyboard control from the composer, which owns focus. */
  ref?: Ref<QuickReplyKeys>;
}

/**
 * What the composer's textarea can do to the panel without taking its focus:
 * move the highlight, and pick what is highlighted. `pick` answers whether it
 * took the Enter — false when there is nothing to pick, so the composer sends.
 */
export interface QuickReplyKeys {
  move(delta: number): void;
  pick(): boolean;
}

/**
 * The list that opens when an agent types `/`.
 *
 * Saving lives here rather than in a settings screen: the moment anyone wants to
 * keep a reply is just after typing it for the third time, and that moment
 * happens in the composer.
 */
export function QuickReplyPanel({ query, contactName, contactId, draft, onPick, onClose, ref }: Props) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [shortcut, setShortcut] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);

  const open = query !== null;
  // The permission the route actually checks, not a role name a tenant may have renamed.
  const isAdmin = can(user, PERMISSION.cannedWrite);

  const replies = useQuery({
    queryKey: ['canned-replies'],
    queryFn: inboxApi.cannedReplies,
    staleTime: 300_000,
    enabled: open,
  });

  // The contact's custom fields, for {{field.key}}. Only fetched while the
  // panel is open and only when some reply actually uses a field.
  const usesFields = (replies.data ?? []).some((r) => /\{\{\s*field\./i.test(r.body));
  const contact = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => inboxApi.contact(contactId!),
    staleTime: 60_000,
    enabled: open && usesFields && typeof contactId === 'number',
  });

  const agentName = user?.displayName ?? null;
  const context = useMemo<QuickReplyContext>(() => ({
    contactName,
    agentName,
    fields: contact.data ? contactAttributes(contact.data) : null,
  }), [contactName, agentName, contact.data]);

  const save = useMutation({
    mutationFn: () => inboxApi.createCannedReply(shortcut, draft.replace(/^\/\S*\s*/, '')),
    onSuccess: () => {
      setShortcut('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['canned-replies'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save it.'),
  });

  const remove = useMutation({
    mutationFn: inboxApi.deleteCannedReply,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['canned-replies'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not delete it.'),
  });

  const matches = useMemo(() => {
    const all = replies.data ?? [];
    const needle = (query ?? '').trim().toLowerCase();
    if (!needle) return all;

    return all.filter(
      (r) => r.shortcut.includes(needle) || r.body.toLowerCase().includes(needle));
  }, [replies.data, query]);

  // Typing narrows the list; the highlight goes back to the top so Enter always
  // picks the row that is drawn as chosen.
  useEffect(() => { setHighlight(0); }, [query]);

  useImperativeHandle(ref, () => ({
    move(delta) {
      if (matches.length === 0) return;
      setHighlight((h) => (h + delta + matches.length) % matches.length);
    },
    pick() {
      const reply = matches[Math.min(highlight, matches.length - 1)];
      if (!reply) return false;
      onPick(fillQuickReply(reply.body, context), attachmentOf(reply));
      return true;
    },
  }), [matches, highlight, onPick, context]);

  if (!open) return null;

  function pick(reply: CannedReply) {
    onPick(fillQuickReply(reply.body, context), attachmentOf(reply));
  }

  return (
    <div className="absolute bottom-full left-0 z-20 mb-2 w-full">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-(--color-line) bg-(--color-surface) shadow-xl">
        <div className="flex items-center gap-2 border-b border-(--color-line) px-3 py-2">
          <h3 className="text-micro tracking-wide text-(--color-muted) uppercase">Quick replies</h3>
          <button
            type="button"
            onClick={onClose}
            className="tap ml-auto rounded px-1 text-micro text-(--color-muted) hover:text-(--color-ink)"
          >
            Esc
          </button>
        </div>

        <ul className="max-h-64 overflow-y-auto">
          {replies.isPending && (
            <li className="px-3 py-3 text-small text-(--color-muted)">Loading…</li>
          )}

          {replies.isSuccess && matches.length === 0 && (
            <li className="px-3 py-3 text-small text-(--color-muted)">
              {replies.data.length === 0
                ? 'No quick replies yet.'
                : `Nothing matches “${query}”.`}
            </li>
          )}

          {matches.map((reply, index) => (
            <li key={reply.id} className="flex items-start gap-1 border-b border-(--color-line) last:border-b-0">
              <button
                type="button"
                onClick={() => pick(reply)}
                onMouseEnter={() => setHighlight(index)}
                aria-selected={index === highlight}
                className={`tap min-w-0 flex-1 px-3 py-2 text-left hover:bg-(--color-sunken) ${
                  index === highlight ? 'bg-(--color-sunken)' : ''
                }`}
              >
                <span className="block font-mono text-small font-medium text-(--color-brand)">
                  /{reply.shortcut}
                </span>
                {/* No `block` here: line-clamp needs display:-webkit-box, and
                    `block` overrides it, so a long reply grows the panel instead
                    of being cut to two lines. */}
                <span className="mt-0.5 line-clamp-2 text-small text-(--color-muted)">
                  {reply.mediaAssetId && <span className="mr-1 rounded bg-(--color-line-dim) px-1 text-micro">File · {reply.mediaFileName ?? 'attached'}</span>}
                  {fillQuickReply(reply.body, context)}
                </span>
              </button>

              {isAdmin && (
                <button
                  type="button"
                  onClick={() => remove.mutate(reply.id)}
                  aria-label={`Delete the /${reply.shortcut} quick reply`}
                  className="tap mt-2 mr-2 shrink-0 rounded px-1.5 py-1 text-(--color-muted) hover:text-(--color-bad)"
                >
                  <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true">
                    <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>

        {/* Offered only when there is something worth keeping — an admin who has
            typed a reply is one field away from never typing it again. */}
        {isAdmin && draft.replace(/^\/\S*\s*/, '').trim().length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-(--color-line) bg-(--color-sunken) px-3 py-2">
            <span className="text-micro text-(--color-muted)">Save this draft as</span>
            <span className="font-mono text-small text-(--color-muted)">/</span>
            <input
              value={shortcut}
              onChange={(e) => setShortcut(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              placeholder="shortcut"
              aria-label="Shortcut for the new quick reply"
              className="tap w-28 min-w-0 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1 font-mono text-small outline-none focus:border-(--color-brand)"
            />
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!shortcut || save.isPending}
              className="tap rounded bg-(--color-brand) px-2.5 py-1 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        {error && (
          <p role="alert" className="border-t border-(--color-line) px-3 py-2 text-micro text-(--color-bad)">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
